import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

class ProductInventory extends Component {
  /** @type {Element | null} */
  #closestSection;

  /** @type {number | undefined} */
  #voUpdateTimer;

  /** @type {AbortController | undefined} */
  #voAbortController;

  connectedCallback() {
    super.connectedCallback();
    this.#closestSection = this.closest('.shopify-section, dialog');
    this.#closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#closestSection?.addEventListener('change', this.#handleVoVariantChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#closestSection?.removeEventListener('change', this.#handleVoVariantChange);
    if (this.#voUpdateTimer) window.clearTimeout(this.#voUpdateTimer);
    this.#voAbortController?.abort();
  }

  /**
   * Handles product select event by updating the inventory.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newInventory = html.querySelector('product-inventory');
        if (!newInventory) return;

        morph(this, newInventory, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-inventory] Event promise rejected:', error);
      });
  };

  /**
   * VO Product Options replaces Horizon's variant-picker and therefore does
   * not emit ProductSelectEvent. Re-render this block from Shopify's selected
   * variant after VO has updated its hidden variant input.
   * @param {Event} event
   */
  #handleVoVariantChange = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.matches('.bcpo-front-image-option, .single-option-selector, #bcpo-select')) return;
    if (event.target.closest('product-card')) return;

    if (this.#voUpdateTimer) window.clearTimeout(this.#voUpdateTimer);
    this.#voUpdateTimer = window.setTimeout(() => this.#syncVoInventory(), 80);
  };

  async #syncVoInventory() {
    const scope = this.#closestSection;
    if (!scope) return;

    const voVariant = scope.querySelector('#bcpo-select');
    const formVariant = scope.querySelector('form[action*="/cart/add"] input[name="id"]');
    const variantId =
      voVariant instanceof HTMLSelectElement && voVariant.value
        ? voVariant.value
        : formVariant instanceof HTMLInputElement
          ? formVariant.value
          : '';
    if (!variantId) return;

    const section = this.closest('.shopify-section');
    const sectionId = section?.id.replace(/^shopify-section-/, '');
    const productForm = scope.querySelector('product-form-component');
    const productUrl = productForm?.dataset.productUrl;
    if (!sectionId || !productUrl) return;

    this.#voAbortController?.abort();
    this.#voAbortController = new AbortController();

    const requestUrl = new URL(productUrl, window.location.origin);
    requestUrl.searchParams.set('variant', variantId);
    requestUrl.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(requestUrl, { signal: this.#voAbortController.signal });
      if (!response.ok) throw new Error(`Inventory section request failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const newInventory = html.querySelector('product-inventory');
      if (!newInventory) return;

      morph(this, newInventory, { childrenOnly: true });
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[product-inventory] VO inventory refresh failed:', error);
    }
  }
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
